import type {
  AdminUser,
  Answer,
  Camera,
  Comment,
  Folder,
  ForumPost,
  ForumSort,
  ImagesPage,
  ImagesQuery,
  LikeStatus,
  ScheduleEvent,
  ScheduleEventInput,
  ScheduleKind,
  Shot,
  Species,
  TeamDocument,
  TeamMember,
} from "./types";

// All paths are relative — same-origin via the vite proxy in dev, served
// behind nginx in prod. No base-URL config needed.

export class HttpError extends Error {
  constructor(public status: number, public body: string) {
    super(`${status} — ${body}`);
  }
}

async function jsonFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const headers: Record<string, string> = {
    Accept: "application/json",
    ...(init?.body && !(init.body instanceof FormData)
      ? { "Content-Type": "application/json" }
      : {}),
    ...((init?.headers as Record<string, string>) ?? {}),
  };
  const res = await fetch(path, {
    credentials: "same-origin",
    ...init,
    headers,
  });
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    throw new HttpError(res.status, body);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/**
 * Access level, matching `users.access_level` server-side. Cumulative: each
 * level includes everything below it, so every check is `>=`.
 */
export const LEVEL_CAMERA_VIEWER = 1;
export const LEVEL_TEAM_MEMBER = 2;
export const LEVEL_ADMIN = 3;

export interface MeResponse {
  username: string;
  authEnabled: boolean;
  /** Name to show in the header; falls back to the email server-side. */
  displayName?: string;
  /** 1 camera viewer, 2 team member, 3 admin. Absent on a 401 body.
   *  Drives which controls are offered — the API re-checks every mutation. */
  accessLevel?: number;
  /** True when this user may read the audit trail. Drives the Audit tab only —
   *  the API re-checks it on every read. */
  admin?: boolean;
  googleEnabled?: boolean;
  googleClientId?: string;
  microsoftEnabled?: boolean;
  microsoftClientId?: string;
  microsoftTenant?: string;
}

export interface AuditQuery {
  from?: string;
  to?: string;
  actor?: string;
  action?: string;
  limit?: number;
}

export interface AuditEvent {
  id: number;
  actor: string;
  action: string;
  entityType: string | null;
  entityId: string | null;
  success: boolean;
  statusCode: number | null;
  method: string | null;
  path: string | null;
  detail: Record<string, unknown> | null;
  ip: string | null;
  userAgent: string | null;
  createdAt: string;
}

export interface AuditPage {
  from: string;
  to: string;
  count: number;
  truncated: boolean;
  actors: string[];
  actions: string[];
  items: AuditEvent[];
}

/**
 * The filename out of a `Content-Disposition`, or null.
 *
 * <p>Prefers `filename*`, which is the UTF-8 form and the only one that
 * survives an å. The plain `filename` is the ASCII fallback the server sends
 * alongside it for old clients, and using it would name every Swedish bundle
 * with underscores where the letters were.
 */
function filenameFrom(header: string | null): string | null {
  if (!header) return null;
  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (utf8) {
    try {
      return decodeURIComponent(utf8[1]);
    } catch {
      // A malformed percent-escape. Fall through to the ASCII form.
    }
  }
  const plain = /filename="([^"]*)"/i.exec(header);
  return plain ? plain[1] : null;
}

function toQS(q: ImagesQuery): string {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) {
    if (v === undefined || v === null) continue;
    params.set(k, String(v));
  }
  const s = params.toString();
  return s ? `?${s}` : "";
}

export const api = {
  listImages(q: ImagesQuery = {}): Promise<ImagesPage> {
    return jsonFetch<ImagesPage>(`/api/images${toQS(q)}`);
  },

  getImage(id: string): Promise<Shot> {
    return jsonFetch<Shot>(`/api/images/${encodeURIComponent(id)}`);
  },

  imageContentUrl(id: string): string {
    return `/api/images/${encodeURIComponent(id)}/content`;
  },

  uploadImage(args: {
    file: Blob;
    filename: string;
    cameraId: string;
    capturedAt: string;
    lat?: number;
    lon?: number;
  }): Promise<{ imageId: string }> {
    const fd = new FormData();
    fd.append("file", args.file, args.filename);
    fd.append("filename", args.filename);
    fd.append("cameraId", args.cameraId);
    fd.append("capturedAt", args.capturedAt);
    if (args.lat !== undefined) fd.append("lat", String(args.lat));
    if (args.lon !== undefined) fd.append("lon", String(args.lon));
    return jsonFetch("/api/images", { method: "POST", body: fd });
  },

  setSpeciesOverride(id: string, value: Species | null): Promise<Shot> {
    return jsonFetch<Shot>(`/api/images/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify({ speciesOverride: value }),
    });
  },

  hideImage(id: string): Promise<Shot> {
    return jsonFetch<Shot>(`/api/images/${encodeURIComponent(id)}/hide`, {
      method: "POST",
    });
  },

  unhideImage(id: string): Promise<Shot> {
    return jsonFetch<Shot>(`/api/images/${encodeURIComponent(id)}/unhide`, {
      method: "POST",
    });
  },

  listCameras(): Promise<Camera[]> {
    return jsonFetch<Camera[]>("/api/cameras");
  },

  getLikes(id: string): Promise<LikeStatus> {
    return jsonFetch<LikeStatus>(`/api/images/${encodeURIComponent(id)}/likes`);
  },

  setLiked(id: string, liked: boolean): Promise<LikeStatus> {
    return jsonFetch<LikeStatus>(`/api/images/${encodeURIComponent(id)}/likes`, {
      method: liked ? "PUT" : "DELETE",
    });
  },

  listComments(id: string): Promise<Comment[]> {
    return jsonFetch<Comment[]>(`/api/images/${encodeURIComponent(id)}/comments`);
  },

  addComment(id: string, body: string): Promise<Comment> {
    return jsonFetch<Comment>(`/api/images/${encodeURIComponent(id)}/comments`, {
      method: "POST",
      body: JSON.stringify({ body }),
    });
  },

  /**
   * Fire-and-forget navigation beacon. Navigation must never be blocked or
   * slowed by its own bookkeeping, so failures are swallowed: a missing audit
   * row is preferable to a screen that won't change because logging was down.
   */
  recordNav(route: string, opts: { date?: string; imageId?: string | null } = {}): void {
    void jsonFetch<void>("/api/audit/nav", {
      method: "POST",
      body: JSON.stringify({
        route,
        date: opts.date ?? null,
        imageId: opts.imageId ?? null,
      }),
    }).catch(() => {});
  },

  listAudit(q: AuditQuery = {}): Promise<AuditPage> {
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(q)) {
      if (v === undefined || v === null || v === "") continue;
      params.set(k, String(v));
    }
    const qs = params.toString();
    return jsonFetch<AuditPage>(`/api/audit${qs ? `?${qs}` : ""}`);
  },

  // ---- Schedule -----------------------------------------------------------

  /**
   * Upcoming Events from `from` (default: server-side today) onwards. Pass `to`
   * for a closed window instead — that is how the screen looks back at a past
   * season without a second endpoint.
   */
  listEvents(q: { from?: string; to?: string; limit?: number } = {}): Promise<ScheduleEvent[]> {
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(q)) {
      if (v === undefined || v === null || v === "") continue;
      params.set(k, String(v));
    }
    const qs = params.toString();
    return jsonFetch<ScheduleEvent[]>(`/api/schedule/events${qs ? `?${qs}` : ""}`);
  },

  createEvent(body: ScheduleEventInput): Promise<ScheduleEvent> {
    return jsonFetch<ScheduleEvent>("/api/schedule/events", {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  /** Replaces every editable field — the form always sends the whole Event back. */
  updateEvent(id: string, body: ScheduleEventInput): Promise<ScheduleEvent> {
    return jsonFetch<ScheduleEvent>(`/api/schedule/events/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    });
  },

  /** Cancel, never delete. The Event stays on the Schedule struck through. */
  cancelEvent(id: string, reason: string | null): Promise<ScheduleEvent> {
    return jsonFetch<ScheduleEvent>(`/api/schedule/events/${encodeURIComponent(id)}/cancel`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    });
  },

  /**
   * Remove an Event, its invites and every answer — admin only, and not
   * undoable. Cancel is what almost everything should use; this is for Events
   * that were never real. Returns the Event as it was, so the screen can name
   * what it just destroyed.
   */
  deleteEvent(id: string): Promise<ScheduleEvent> {
    return jsonFetch<ScheduleEvent>(`/api/schedule/events/${encodeURIComponent(id)}`, {
      method: "DELETE",
    });
  },

  /** Your own answer. Returns the whole Event so the tally redraws from the server. */
  answerEvent(id: string, answer: Answer, comment: string | null): Promise<ScheduleEvent> {
    return jsonFetch<ScheduleEvent>(`/api/schedule/events/${encodeURIComponent(id)}/response`, {
      method: "PUT",
      body: JSON.stringify({ answer, comment }),
    });
  },

  /**
   * Copy an Event onto other days. Returns the new Events, soonest first.
   * Responses and the cancellation are deliberately not carried over.
   */
  copyEvent(id: string, dates: string[]): Promise<ScheduleEvent[]> {
    return jsonFetch<ScheduleEvent[]>(`/api/schedule/events/${encodeURIComponent(id)}/copy`, {
      method: "POST",
      body: JSON.stringify({ dates }),
    });
  },

  /**
   * Take your answer back, returning you to Pending. DELETE because that is
   * what happens — Pending is the absence of a Response, not a third value.
   */
  withdrawAnswer(id: string): Promise<ScheduleEvent> {
    return jsonFetch<ScheduleEvent>(`/api/schedule/events/${encodeURIComponent(id)}/response`, {
      method: "DELETE",
    });
  },

  listEventKinds(): Promise<ScheduleKind[]> {
    return jsonFetch<ScheduleKind[]>("/api/schedule/kinds");
  },

  /**
   * The hunting team, for the invitee picker. Deliberately not `listUsers` —
   * that one is admin-only and carries everyone's email address, where this
   * carries a name and an id and is open to any team member.
   */
  listTeamMembers(): Promise<TeamMember[]> {
    return jsonFetch<TeamMember[]>("/api/schedule/team");
  },

  pendingEventCount(): Promise<{ count: number }> {
    return jsonFetch<{ count: number }>("/api/schedule/pending");
  },

  // ---- Forum --------------------------------------------------------------

  /**
   * The feed, each Post complete except for its comment thread.
   *
   * `sort` is "latest" (newest written, the default) or "updated" (most recently
   * active — an edit or a comment both count).
   */
  listPosts(opts: { sort?: ForumSort; limit?: number } = {}): Promise<ForumPost[]> {
    const params = new URLSearchParams();
    if (opts.sort) params.set("sort", opts.sort);
    if (opts.limit) params.set("limit", String(opts.limit));
    const qs = params.toString();
    return jsonFetch<ForumPost[]>(`/api/forum/posts${qs ? `?${qs}` : ""}`);
  },

  /**
   * Write a Post, with its images in the same request.
   *
   * <p>Multipart, and the text half goes as a `Blob` with an explicit JSON
   * content type — Spring binds `@RequestPart("post")` by that header, and a
   * plain string part arrives as `text/plain` and fails to bind.
   *
   * <p>No `Content-Type` is set on the request itself on purpose: the browser
   * has to add the multipart boundary, and setting it by hand produces a body
   * the server cannot parse.
   */
  createPost(post: { title: string; body: string | null }, images: File[]): Promise<ForumPost> {
    const form = new FormData();
    form.append("post", new Blob([JSON.stringify(post)], { type: "application/json" }));
    for (const file of images) form.append("images", file, file.name);
    return jsonFetch<ForumPost>("/api/forum/posts", { method: "POST", body: form });
  },

  /** Replaces the title and body. Images are not editable — see ForumService. */
  updatePost(id: string, post: { title: string; body: string | null }): Promise<ForumPost> {
    return jsonFetch<ForumPost>(`/api/forum/posts/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(post),
    });
  },

  /** Removes the Post with its images, likes and comments. Author or admin. */
  deletePost(id: string): Promise<ForumPost> {
    return jsonFetch<ForumPost>(`/api/forum/posts/${encodeURIComponent(id)}`, {
      method: "DELETE",
    });
  },

  /** Where a Post image's bytes live. Used directly as an `<img src>`. */
  postImageUrl(imageId: string): string {
    return `/api/forum/images/${encodeURIComponent(imageId)}/content`;
  },

  getPostLikes(id: string): Promise<LikeStatus> {
    return jsonFetch<LikeStatus>(`/api/forum/posts/${encodeURIComponent(id)}/likes`);
  },

  setPostLiked(id: string, liked: boolean): Promise<LikeStatus> {
    return jsonFetch<LikeStatus>(`/api/forum/posts/${encodeURIComponent(id)}/likes`, {
      method: liked ? "PUT" : "DELETE",
    });
  },

  listPostComments(id: string): Promise<Comment[]> {
    return jsonFetch<Comment[]>(`/api/forum/posts/${encodeURIComponent(id)}/comments`);
  },

  addPostComment(id: string, body: string): Promise<Comment> {
    return jsonFetch<Comment>(`/api/forum/posts/${encodeURIComponent(id)}/comments`, {
      method: "POST",
      body: JSON.stringify({ body }),
    });
  },

  /** Reword your own comment. Author only — an admin is no exception. */
  editPostComment(id: string, commentId: number, body: string): Promise<Comment> {
    return jsonFetch<Comment>(
      `/api/forum/posts/${encodeURIComponent(id)}/comments/${commentId}`,
      { method: "PATCH", body: JSON.stringify({ body }) },
    );
  },

  /** Remove your own comment. Answers 204, so there is nothing to read back. */
  deletePostComment(id: string, commentId: number): Promise<void> {
    return jsonFetch<void>(
      `/api/forum/posts/${encodeURIComponent(id)}/comments/${commentId}`,
      { method: "DELETE" },
    );
  },

  // ---- Documents ----------------------------------------------------------

  /**
   * The whole Folder tree, flat. One fetch: it is a few dozen rows, and a
   * per-level API would make opening a Folder a round trip for something the
   * client already has.
   */
  listFolders(): Promise<Folder[]> {
    return jsonFetch<Folder[]>("/api/documents/folders");
  },

  /** `parentId` null creates it at the top level. */
  createFolder(name: string, parentId: string | null): Promise<Folder> {
    return jsonFetch<Folder>("/api/documents/folders", {
      method: "POST",
      body: JSON.stringify({ name, parentId }),
    });
  },

  /** Only an empty one. A folder with anything in it is a 400 naming what. */
  deleteFolder(id: string): Promise<Folder> {
    return jsonFetch<Folder>(`/api/documents/folders/${encodeURIComponent(id)}`, {
      method: "DELETE",
    });
  },

  /** One Folder's Documents, newest first. `folderId` null is the root. */
  listDocuments(folderId: string | null): Promise<TeamDocument[]> {
    const qs = folderId ? `?folderId=${encodeURIComponent(folderId)}` : "";
    return jsonFetch<TeamDocument[]>(`/api/documents${qs}`);
  },

  /**
   * Upload files into a Folder.
   *
   * <p>No `Content-Type` header on purpose, as with `createPost`: the browser
   * has to add the multipart boundary, and setting it by hand produces a body
   * the server cannot parse.
   */
  uploadDocuments(folderId: string | null, files: File[]): Promise<TeamDocument[]> {
    const form = new FormData();
    for (const file of files) form.append("files", file, file.name);
    const qs = folderId ? `?folderId=${encodeURIComponent(folderId)}` : "";
    return jsonFetch<TeamDocument[]>(`/api/documents${qs}`, { method: "POST", body: form });
  },

  deleteDocument(id: string): Promise<TeamDocument> {
    return jsonFetch<TeamDocument>(`/api/documents/${encodeURIComponent(id)}`, {
      method: "DELETE",
    });
  },

  /** Where a Document's bytes are. Always answers with an attachment disposition. */
  documentContentUrl(id: string): string {
    return `/api/documents/${encodeURIComponent(id)}/content`;
  },

  /** The preview square, for Documents whose `hasThumbnail` is true. */
  documentThumbnailUrl(id: string): string {
    return `/api/documents/${encodeURIComponent(id)}/thumbnail`;
  },

  /**
   * Download several Documents as one ZIP.
   *
   * <p>Not `jsonFetch`: the response is an archive, and the browser's own
   * download machinery is what should receive it. A POST cannot be handed to
   * `window.location`, so the blob is fetched and given to a synthetic link —
   * which is also what lets a 403 surface as an error instead of navigating the
   * tab to a JSON error page.
   */
  async downloadBundle(documentIds: string[]): Promise<void> {
    const res = await fetch("/api/documents/bundle", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ documentIds }),
    });
    if (!res.ok) {
      throw new HttpError(res.status, await res.text().catch(() => ""));
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    try {
      const a = document.createElement("a");
      a.href = url;
      a.download = filenameFrom(res.headers.get("Content-Disposition")) ?? "dokument.zip";
      document.body.appendChild(a);
      a.click();
      a.remove();
    } finally {
      // Not immediately: Safari has not started reading the blob when click()
      // returns, and revoking under it produces a download of zero bytes.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    }
  },

  /**
   * Refile Documents into another Folder. `folderId` null moves them to the
   * root. Any team member may — unlike deleting, which is uploader-or-admin.
   *
   * <p>One request for the whole selection: moving twelve files is one act, and
   * a per-file call would make a half-finished move a normal outcome.
   */
  moveDocuments(documentIds: string[], folderId: string | null): Promise<TeamDocument[]> {
    return jsonFetch<TeamDocument[]>("/api/documents/move", {
      method: "POST",
      body: JSON.stringify({ documentIds, folderId }),
    });
  },

  /** Try the Google Drive copy again. Answers 502 when Drive refused. */
  retryDriveCopy(id: string): Promise<TeamDocument> {
    return jsonFetch<TeamDocument>(`/api/documents/${encodeURIComponent(id)}/drive`, {
      method: "POST",
    });
  },

  // ---- User management ----------------------------------------------------

  listUsers(): Promise<AdminUser[]> {
    return jsonFetch<AdminUser[]>("/api/admin/users");
  },

  createUser(body: {
    email: string;
    password?: string;
    accessLevel: number;
    displayName?: string;
  }): Promise<AdminUser> {
    return jsonFetch<AdminUser>("/api/admin/users", {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  /**
   * Partial update — send only what changed. There is no email field: it is
   * immutable after creation (ADR-0002).
   */
  updateUser(
    id: string,
    body: { accessLevel?: number; displayName?: string; active?: boolean },
  ): Promise<AdminUser> {
    return jsonFetch<AdminUser>(`/api/admin/users/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    });
  },

  resetUserPassword(id: string, password: string): Promise<void> {
    return jsonFetch<void>(`/api/admin/users/${encodeURIComponent(id)}/password`, {
      method: "POST",
      body: JSON.stringify({ password }),
    });
  },

  /** Change your own. `currentPassword` may be omitted only by an SSO-only user. */
  changeMyPassword(currentPassword: string | null, newPassword: string): Promise<void> {
    return jsonFetch<void>("/api/me/password", {
      method: "POST",
      body: JSON.stringify({ currentPassword, newPassword }),
    });
  },

  notificationsSince(ts: string): Promise<Shot[]> {
    return jsonFetch<Shot[]>(`/api/notifications/since?ts=${encodeURIComponent(ts)}`);
  },

  me(): Promise<MeResponse> {
    return jsonFetch<MeResponse>("/api/auth/me");
  },

  login(username: string, password: string): Promise<MeResponse> {
    return jsonFetch<MeResponse>("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ username, password }),
    });
  },

  googleLogin(credential: string): Promise<MeResponse> {
    return jsonFetch<MeResponse>("/api/auth/google", {
      method: "POST",
      body: JSON.stringify({ credential }),
    });
  },

  microsoftLogin(credential: string): Promise<MeResponse> {
    return jsonFetch<MeResponse>("/api/auth/microsoft", {
      method: "POST",
      body: JSON.stringify({ credential }),
    });
  },

  logout(): Promise<void> {
    return jsonFetch<void>("/api/auth/logout", { method: "POST" });
  },
};
